import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { MY_TASKS_LIMIT, isChecklistItemDone, taskDueBucket, type ClientTask } from '@peles/shared';
import { useMyPortalTasks, useToggleClientTask } from '../../hooks/usePortal';
import { useLocalDay } from '../../hooks/useDashboard';
import type { PortalTaskScope } from '../../api/portal';
import { TaskCheckbox, TaskDueBadge } from '../tasks/TaskRow';

/** How many open tasks show before "Show more". */
export const MY_TASKS_PAGE = 10;

export interface MyTasksCardProps {
  /** Whose portal: ticks go through the client's or the vendor's routes. */
  scope: PortalTaskScope;
  /** Where a task's event opens — its tasks in that portal. */
  eventPath: (eventId: string) => string;
}

function MyTaskRow({
  task,
  scope,
  todayKey,
  eventPath,
  onSaved,
}: {
  task: ClientTask;
  scope: PortalTaskScope;
  todayKey: string;
  eventPath: (eventId: string) => string;
  /** Told how a tick went — the row has left the list by then. */
  onSaved: (task: ClientTask, ok: boolean) => void;
}) {
  const toggle = useToggleClientTask(task.event?.id ?? '', scope);
  const done = isChecklistItemDone(task);
  // `mutateAsync`, not `mutate`'s callbacks: a ticked task leaves the list at
  // once, and an unmounted row's callbacks never run.
  const save = () =>
    toggle.mutateAsync({ taskId: task.id, isDone: !done }).then(
      () => onSaved(task, true),
      () => onSaved(task, false),
    );
  return (
    <li className="flex items-start gap-3 px-4 py-3" data-testid={`my-task-${task.id}`}>
      <TaskCheckbox
        checked={done}
        onChange={() => void save()}
        label={done ? `Reopen ${task.title}` : `Complete ${task.title}`}
      />
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-6 ${done ? 'line-through text-gray-400' : 'text-gray-900'}`}>
          {task.title}
        </p>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-0.5">
          {task.dueDate && !done && <TaskDueBadge dueDate={task.dueDate} todayKey={todayKey} />}
          {task.event && (
            <Link
              to={eventPath(task.event.id)}
              className="text-xs text-primary-600 hover:text-primary-700"
            >
              {task.event.name}
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * "My tasks" on a client's or vendor's dashboard: what is assigned to them on
 * every event they are on, soonest first, each ticked off in place and linked
 * to its event. Says nothing while there is nothing to do.
 */
export function MyTasksCard({ scope, eventPath }: MyTasksCardProps) {
  const { date: todayKey } = useLocalDay();
  const { data: tasks = [], isLoading, isError, refetch } = useMyPortalTasks();
  const [shown, setShown] = useState(MY_TASKS_PAGE);
  const [failed, setFailed] = useState<ClientTask | null>(null);
  // A failure is cleared by that task saving, never by another one's.
  const onSaved = (task: ClientTask, ok: boolean) =>
    setFailed((current) => (ok ? (current?.id === task.id ? null : current) : task));

  if (isLoading) {
    return (
      <div
        className="h-24 rounded-xl bg-white border border-gray-200 animate-pulse"
        aria-label="Loading your tasks"
      />
    );
  }
  if (isError) {
    return (
      <div className="rounded-xl bg-white border border-gray-200 px-4 py-3 text-sm text-gray-600">
        Your tasks could not be loaded.{' '}
        <button
          type="button"
          onClick={() => refetch()}
          className="text-primary-600 hover:text-primary-700"
        >
          Try again
        </button>
      </div>
    );
  }

  const open = tasks.filter((t) => !isChecklistItemDone(t));
  if (open.length === 0 && !failed) return null;
  const overdue = open.filter((t) => taskDueBucket(t.dueDate, todayKey) === 'overdue').length;
  // The server sends at most MY_TASKS_LIMIT, soonest first: a full list may
  // have more, and so may its overdue count when every task sent is overdue.
  const more = tasks.length >= MY_TASKS_LIMIT;
  const plus = (partial: boolean) => (partial ? '+' : '');

  return (
    <section className="bg-white rounded-xl border border-gray-200" aria-label="My tasks">
      <header className="flex items-center gap-2 px-4 pt-4 pb-2">
        <ListChecks size={18} className="text-primary-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">My tasks</h2>
          <p className="text-sm text-gray-500" data-testid="my-tasks-summary">
            {open.length}
            {plus(more)} to do
            {overdue ? ` · ${overdue}${plus(more && overdue === open.length)} overdue` : ''}
          </p>
        </div>
      </header>
      {failed && (
        <div
          role="alert"
          className="mx-4 mb-2 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          <span className="flex-1">
            “{failed.title}” could not be saved — it is back on your list. Tick it again to retry.
          </span>
          <button
            type="button"
            onClick={() => setFailed(null)}
            className="min-h-11 sm:min-h-0 font-medium text-red-700 hover:text-red-900"
          >
            Dismiss
          </button>
        </div>
      )}
      <ul className="divide-y divide-gray-100 border-t border-gray-100">
        {open.slice(0, shown).map((task) => (
          <MyTaskRow
            key={task.id}
            task={task}
            scope={scope}
            todayKey={todayKey}
            eventPath={eventPath}
            onSaved={onSaved}
          />
        ))}
      </ul>
      {open.length > shown && (
        <button
          type="button"
          onClick={() => setShown((n) => n + MY_TASKS_PAGE)}
          className="w-full min-h-11 sm:min-h-9 border-t border-gray-100 text-sm text-primary-600 hover:bg-gray-50 rounded-b-xl"
        >
          Show more ({open.length - shown})
        </button>
      )}
    </section>
  );
}
